import { useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import clsx from "clsx";
import { ThemeToggle } from "./ThemeToggle";
import { BrandLogo } from "./BrandLogo";
import { MobileNav } from "./MobileNav";
import { useAuthStore } from "../../lib/stores/auth";
import { useCart } from "../../features/cart/queries";
import { useCategories } from "../../features/catalog/queries";
import { useStoreConfig } from "../../features/config/queries";
import { useHeroOverlapStore } from "../../lib/stores/heroOverlap";
import { SearchSuggestionsDropdown } from "../search/SearchSuggestionsDropdown";
import { useDebouncedValue } from "../../lib/useDebouncedValue";

// Trailing links that aren't product categories, so they're not managed via
// Admin → Categories — always shown after whatever categories are active there.
const STATIC_TRAILING_LINKS = [
  { to: "/bundles", label: "Bundles" },
  { to: "/offers", label: "Offers" }
];

export function Header() {
  const isAuthenticated = useAuthStore((s) => s.status === "authenticated");
  const { data: config } = useStoreConfig();
  const siteName = config?.storeName || "Latif General Store";
  const [siteNameFirst, ...siteNameRest] = siteName.split(" ");
  const { data: categories } = useCategories();
  const navLinks = [
    ...(categories ?? []).map((c) => ({ to: `/category/${c.slug}`, label: c.name })),
    ...STATIC_TRAILING_LINKS
  ];
  const { data: cart } = useCart();
  const itemCount = cart?.items.reduce((sum, i) => sum + i.quantity, 0) ?? 0;
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const overDarkHero = useHeroOverlapStore((s) => s.overDarkHero);

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
  };

  const submitSearch = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    closeSearch();
  };

  const iconButtonClass = clsx(
    "h-11 w-11 max-[374px]:h-10 max-[374px]:w-10 rounded-full flex items-center justify-center transition-colors duration-300 ease-physical active:scale-95",
    overDarkHero ? "text-[#f6f1e7]/70 hover:text-[#f6f1e7] hover:bg-white/10" : "text-ink-muted hover:text-ink hover:bg-surface"
  );

  return (
    // Positioning/inset shell — padding here (not margin on the pill itself)
    // creates the floating gap from the viewport edges. This wrapper is a
    // flex item of PublicLayout's flex-col root, so it stretches to full
    // width via the default flex behavior; the actual bordered/rounded bar
    // below it is then a normal block element (mx-auto + max-w-7xl), which
    // centers exactly like any other section on the page.
    <div className="sticky top-3 sm:top-4 z-40 px-3 sm:px-4 lg:px-6">
    <header
      className={clsx(
        "mx-auto max-w-7xl rounded-full border shadow-lg transition-colors duration-500",
        overDarkHero
          ? "bg-[#050813]/90 backdrop-blur-xl border-white/10 shadow-black/40"
          : "glass border-border shadow-black/5"
      )}
    >
      <div
        className={clsx(
          "h-16 px-5 sm:px-7 flex items-center justify-between gap-6 max-[374px]:gap-3 max-[374px]:px-4"
        )}
      >
        <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label={`${siteName} home`}>
          <BrandLogo variant="icon" className="h-9 w-auto shrink-0" forceTheme={overDarkHero ? "dark" : undefined} />
          <span
            className={clsx(
              "hidden lg:block font-serif text-base tracking-tight leading-none whitespace-nowrap",
              overDarkHero ? "text-[#f6f1e7]" : "text-ink"
            )}
          >
            {siteNameFirst}{" "}
            {siteNameRest.length > 0 && (
              <span className={overDarkHero ? "text-[#f6f1e7]/60" : "text-ink-muted"}>{siteNameRest.join(" ")}</span>
            )}
          </span>
        </Link>

        {searchOpen ? (
          <div className="relative flex-1 flex items-center gap-2">
            <input
              ref={searchInputRef}
              type="search"
              autoFocus
              aria-label="Search products"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
                if (e.key === "Escape") closeSearch();
              }}
              placeholder="Search products…"
              className={clsx(
                "w-full min-w-0 h-10 appearance-none rounded-full border px-4 text-sm focus:outline-none focus:ring-2",
                overDarkHero
                  ? "border-white/15 bg-white/[0.06] text-[#f6f1e7] placeholder:text-[#f6f1e7]/40 focus:ring-[#dcb662]/40"
                  : "border-border bg-surface text-ink placeholder:text-ink-muted/60 focus:ring-accent/40"
              )}
            />
            <SearchSuggestionsDropdown
              query={debouncedQuery}
              onNavigate={closeSearch}
              variant={overDarkHero ? "cinematic" : "adaptive"}
            />
            <button type="button" onClick={closeSearch} aria-label="Close search" className={clsx(iconButtonClass, "shrink-0")}>
              <X size={18} />
            </button>
          </div>
        ) : (
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  clsx(
                    "text-sm font-medium transition-colors duration-300 ease-physical",
                    overDarkHero
                      ? isActive
                        ? "text-[#dcb662]"
                        : "text-[#f6f1e7]/60 hover:text-[#f6f1e7]"
                      : isActive
                        ? "text-accent"
                        : "text-ink-muted hover:text-ink"
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-1 max-[374px]:gap-0.5">
          {!searchOpen && (
            <>
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open menu"
                className={clsx(iconButtonClass, "lg:hidden")}
              >
                <Menu size={18} />
              </button>
              <button type="button" onClick={() => setSearchOpen(true)} aria-label="Search" className={iconButtonClass}>
                <Search size={18} />
              </button>
              <div className="hidden lg:block">
                <ThemeToggle forceDark={overDarkHero} />
              </div>
              <Link to="/account/wishlist" aria-label="Wishlist" className={iconButtonClass}>
                <Heart size={18} />
              </Link>
              <Link to="/cart" aria-label="Cart" className={clsx(iconButtonClass, "relative")}>
                <ShoppingBag size={18} />
                {itemCount > 0 && (
                  <span
                    className={clsx(
                      "absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center",
                      overDarkHero ? "bg-[#dcb662] text-[#0b1330]" : "bg-accent text-ink"
                    )}
                  >
                    {itemCount}
                  </span>
                )}
              </Link>
              <Link to={isAuthenticated ? "/account" : "/login"} aria-label="Account" className={iconButtonClass}>
                <User size={18} />
              </Link>
            </>
          )}
        </div>
      </div>

      <MobileNav open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} links={navLinks} />
    </header>
    </div>
  );
}
